import { useEffect } from 'react';
import ReactGA from 'react-ga4';
import Page from '../components/Page';

export default function PrivacyPage() {
    useEffect(() => {
        ReactGA.send('pageview');
    }, []);

    return (
        <Page seo={{
            title: 'Privacy Policy — Scheduly',
            description: 'Learn how Scheduly collects, uses, and protects your information when you use our church scheduling and volunteer management software.',
            canonical: 'https://scheduly.org/privacy',
        }}>
            <div className="max-w-3xl mx-auto px-6 py-16">
                <h1 className="text-3xl font-black text-slate-900 tracking-tight mb-2">Privacy Policy</h1>
                <p className="text-sm text-slate-500 mb-10">Last updated: April 1, 2026</p>

                <div className="prose prose-slate max-w-none space-y-8 text-sm text-slate-700 leading-relaxed">

                    <section>
                        <h2 className="text-lg font-bold text-slate-900 mb-2">1. Information We Collect</h2>
                        <p>When you use Scheduly, we collect the following types of information:</p>
                        <ul className="list-disc pl-5 mt-2 space-y-1">
                            <li><strong>Account information:</strong> Name, email address, and organization name provided at registration.</li>
                            <li><strong>Member data:</strong> Contact information for church members that you add to the Service.</li>
                            <li><strong>Usage data:</strong> Events created, volunteer assignments, attendance records, and notification history.</li>
                            <li><strong>Payment information:</strong> Billing details handled securely by Stripe. We do not store full payment card numbers.</li>
                            <li><strong>Device and browser information:</strong> IP address, browser type, and usage logs collected for security and analytics.</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-lg font-bold text-slate-900 mb-2">2. How We Use Your Information</h2>
                        <p>We use the information we collect to:</p>
                        <ul className="list-disc pl-5 mt-2 space-y-1">
                            <li>Provide and maintain the Service</li>
                            <li>Send notifications and alerts on your behalf to church members</li>
                            <li>Process payments and manage subscriptions</li>
                            <li>Improve the Service and fix bugs</li>
                            <li>Respond to support requests and inquiries</li>
                            <li>Comply with legal obligations</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-lg font-bold text-slate-900 mb-2">3. Third-Party Services</h2>
                        <p>We use the following third-party services to operate Scheduly:</p>
                        <ul className="list-disc pl-5 mt-2 space-y-1">
                            <li><strong>Firebase (Google):</strong> Authentication, database, and hosting services.</li>
                            <li><strong>Stripe:</strong> Payment processing. Stripe's privacy policy applies to payment data.</li>
                            <li><strong>Google Analytics:</strong> Anonymous usage analytics to understand how the Service is used.</li>
                            <li><strong>SMS providers:</strong> Used to deliver text message notifications to church members.</li>
                        </ul>
                        <p className="mt-2">We do not sell your data to third parties.</p>
                    </section>

                    <section>
                        <h2 className="text-lg font-bold text-slate-900 mb-2">4. Member Data</h2>
                        <p>Member contact information (names, phone numbers, email addresses) entered into Scheduly is used solely to provide the scheduling and communication features of the Service. This data belongs to your organization. We do not use member contact information for our own marketing purposes.</p>
                    </section>

                    <section>
                        <h2 className="text-lg font-bold text-slate-900 mb-2">5. Data Security</h2>
                        <p>We take reasonable measures to protect your information from unauthorized access, including encrypted data transmission (HTTPS) and secure authentication. However, no method of transmission over the internet is 100% secure.</p>
                    </section>

                    <section>
                        <h2 className="text-lg font-bold text-slate-900 mb-2">6. Data Retention</h2>
                        <p>We retain your data for as long as your account is active or as needed to provide the Service. If you cancel your account, you may request deletion of your data by contacting us. We will delete your data within 30 days of the request, except where we are required to retain it for legal or compliance purposes.</p>
                    </section>

                    <section>
                        <h2 className="text-lg font-bold text-slate-900 mb-2">7. Cookies</h2>
                        <p>We use cookies and similar technologies to maintain session state and collect analytics data. You can control cookie settings through your browser. Disabling cookies may affect functionality of the Service.</p>
                    </section>

                    <section>
                        <h2 className="text-lg font-bold text-slate-900 mb-2">8. Children's Privacy</h2>
                        <p>The Service is not directed to children under 13. We do not knowingly collect personal information from children under 13. If you believe we have inadvertently collected such information, please contact us.</p>
                    </section>

                    <section>
                        <h2 className="text-lg font-bold text-slate-900 mb-2">9. Changes to This Policy</h2>
                        <p>We may update this Privacy Policy from time to time. We will notify you of material changes by email or by posting a notice on the Service. Continued use of the Service after changes constitutes acceptance of the revised policy.</p>
                    </section>

                    <section>
                        <h2 className="text-lg font-bold text-slate-900 mb-2">10. Contact</h2>
                        <p>If you have questions or concerns about this Privacy Policy, contact us at <a href="mailto:schedulyevents@gmail.com" className="text-indigo-600 underline">schedulyevents@gmail.com</a>.</p>
                    </section>

                </div>
            </div>
        </Page>
    );
}
